<template>
  <section
    id="reference"
    class="references"
  >
    <div>
      <article class="default-text">
        <img
          src="/images/logo-white.svg"
          alt="logo"
        >
        <slot name="about" />
      </article>
      <v-tabs
        v-model="selectedCategory"
        bg-color="none"
        height="52px"
        hide-slider
        show-arrows
        grow
      >
        <v-tab
          value="metal"
          active-color="#fcb050"
        >
          Ocel
        </v-tab>
        <v-tab
          value="wood"
          active-color="#fcb050"
        >
          Dřevo
        </v-tab>
        <v-tab
          value="garden"
          active-color="#fcb050"
        >
          Zahrada
        </v-tab>
        <v-tab
          value="other"
          active-color="#fcb050"
        >
          Různé
        </v-tab>
      </v-tabs>

      <div class="tiles gallery">
        <div
          v-for="group in displayedGroups"
          :key="group[0].galleryId"
          class="flip-card"
        >
          <div>
            <!-- front of the card: thumbnail -->
            <div class="flip-card-front">
              <img
                :src="thumbUrl(group[0].contentUrl)"
                :alt="group[0].description?.replace(/<[^>]*>?/gm, '').slice(0, 50)"
              >
            </div>

            <!-- back of the card: description + lightbox link(s) -->
            <div class="flip-card-back">
              <div
                class="default-text"
                v-html="group[0].description"
              />

              <div>
                <!-- first (visible) lightbox trigger -->
                <a
                  class="glightbox"
                  :data-gallery="group[0].galleryId"
                  :href="group[0].contentUrl"
                >
                  Zobrazit
                </a>

                <!-- any additional (hidden) slides for this same gallery -->
                <template
                  v-for="item in group.slice(1)"
                  :key="item.mediaId"
                >
                  <a
                    class="glightbox"
                    :data-gallery="group[0].galleryId"
                    :href="item.contentUrl"
                    style="display: none;"
                  />
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>

      <span
        v-if="hasMore"
        class="show-more"
      >
        <a @click.prevent="visibleCount += increment">Zobrazit další</a>
      </span>
    </div>
  </section>
</template>

<script setup lang="ts">
import { onMounted, ref, computed, watch, nextTick } from 'vue'
import GLightbox from 'glightbox'

/** A single media item in a gallery */
interface GalleryItem {
  galleryId: number
  mediaId: number
  category: 'metal' | 'wood' | 'garden' | 'other'
  contentUrl: string
  description: string
}

/** Raw media payload as passed in props */
interface RawMedia {
  id: number
  contentUrl: string
  description: string
}

/** Gallery group with ID and media items */
interface GalleryGroup {
  galleryId: number
  media: RawMedia[]
}

const props = defineProps<{
  galleries: Record<GalleryItem['category'], GalleryGroup[]>
}>()

/**
 * Flatten all media entries into a single array of GalleryItem.
 * Galleries are now arrays, preserving sort order from backend.
 */
const allGalleries = computed<GalleryItem[]>(() => {
  const list: GalleryItem[] = []
  for (const categoryKey in props.galleries) {
    const category = categoryKey as GalleryItem['category']
    const galleryGroups = props.galleries[category]

    // Iterate over array of gallery groups (preserves order!)
    for (const galleryGroup of galleryGroups) {
      for (const media of galleryGroup.media) {
        list.push({
          galleryId: galleryGroup.galleryId,
          mediaId: media.id,
          category,
          contentUrl: media.contentUrl,
          description: media.description
        })
      }
    }
  }
  return list
})

const selectedCategory = ref<GalleryItem['category']>('metal')
const visibleCount = ref(6)
const increment = 6

/**
 * Group allGallery items by galleryId for the current category.
 */
const allGroupsByCategory = computed<GalleryItem[][]>(() => {
  const map = new Map<number, GalleryItem[]>()
  allGalleries.value
    .filter(item => item.category === selectedCategory.value)
    .forEach(item => {
      if (!map.has(item.galleryId)) map.set(item.galleryId, [])
      map.get(item.galleryId)!.push(item)
    })
  return Array.from(map.values())
})

/**
 * Slice the grouped galleries to only show up to visibleCount groups.
 */
const displayedGroups = computed<GalleryItem[][]>(
  () => allGroupsByCategory.value.slice(0, visibleCount.value)
)

/**
 * Whether there are more groups available beyond visibleCount.
 */
const hasMore = computed(() =>
  visibleCount.value < allGroupsByCategory.value.length
)

/**
 * Generate a square thumbnail URL from the full media URL.
 */
function thumbUrl(url?: string): string {
  return url ? url.replace('/media/', '/media/cache/square/') : ''
}

let lightbox: ReturnType<typeof GLightbox>

onMounted(() => {
  lightbox = GLightbox({
    selector: '.glightbox',
    loop: true,
    autoplayVideos: false
  })
})

watch([selectedCategory, visibleCount], async () => {
  await nextTick()
  lightbox.reload()
})

console.log(allGalleries.value)
</script>
